import React, { useState } from 'react';
import { Edit3, X, AlertCircle } from 'lucide-react';

interface OptionsManagerProps {
  options: { name: string; value: string }[];
  onChange: (options: { name: string; value: string }[]) => void;
  color: string;
  error?: string;
}

const OptionsManager: React.FC<OptionsManagerProps> = ({ options, onChange, color, error }) => {
  const [optionText, setOptionText] = useState('');
  const [editingIndex, setEditingIndex] = useState<number | null>(null);

  const addOption = () => {
    if (!optionText.trim()) return;

    const newOption = {
      name: optionText.trim(),
      value: optionText.trim().toLowerCase().replace(/\s+/g, '_')
    };

    if (editingIndex !== null) {
      // Edit existing option
      const updatedOptions = [...options];
      updatedOptions[editingIndex] = newOption;
      onChange(updatedOptions);
      setEditingIndex(null);
    } else {
      // Add new option
      onChange([...options, newOption]);
    }

    setOptionText('');
  };

  const editOption = (index: number) => {
    const option = options[index];
    if (option) {
      setOptionText(option.name);
      setEditingIndex(index);
    }
  };

  const removeOption = (index: number) => {
    onChange(options.filter((_, i) => i !== index));
  };

  return (
    <div style={{ marginBottom: '16px' }}>
      <label style={{
        display: 'block',
        marginBottom: '8px',
        fontSize: '13px',
        fontWeight: '500',
        color: '#374151'
      }}>
        Options <span style={{ color: '#dc2626' }}>*</span>
      </label>

      {/* Add Option Input */}
      <div style={{
        display: 'flex',
        gap: '8px',
        marginBottom: '8px'
      }}>
        <input
          type="text"
          value={optionText}
          onChange={(e) => setOptionText(e.target.value)}
          placeholder="Enter option text"
          style={{
            flex: 1,
            padding: '6px 8px',
            border: '1px solid #d1d5db',
            borderRadius: '4px',
            fontSize: '12px',
            outline: 'none'
          }}
          onKeyPress={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addOption();
            }
          }}
        />
        <button
          type="button"
          onClick={addOption}
          disabled={!optionText.trim()}
          style={{
            backgroundColor: optionText.trim() ? color : '#d1d5db',
            color: '#ffffff',
            border: 'none',
            borderRadius: '4px',
            padding: '6px 12px',
            fontSize: '12px',
            fontWeight: '500',
            cursor: optionText.trim() ? 'pointer' : 'not-allowed'
          }}
        >
          {editingIndex !== null ? 'Update' : 'Add'}
        </button>
      </div>

      {/* Options List */}
      {options && options.length > 0 && (
        <div style={{
          backgroundColor: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '4px',
          padding: '8px',
          maxHeight: '120px',
          overflowY: 'auto'
        }}>
          {options.map((option, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '4px 6px',
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '3px',
                marginBottom: '4px',
                fontSize: '12px'
              }}
            >
              <span style={{ flex: 1 }}>{option.name}</span>
              <div style={{ display: 'flex', gap: '4px' }}>
                <button
                  type="button"
                  onClick={() => editOption(index)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    color: '#6b7280'
                  }}
                >
                  <Edit3 size={10} />
                </button>
                <button
                  type="button"
                  onClick={() => removeOption(index)}
                  style={{
                    backgroundColor: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    padding: '2px',
                    color: '#dc2626'
                  }}
                >
                  <X size={10} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '4px',
          marginTop: '4px',
          fontSize: '11px',
          color: '#dc2626'
        }}>
          <AlertCircle size={12} />
          {error}
        </div>
      )}
    </div>
  );
};

export default OptionsManager;
